import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Input,
  Textarea,
  Button,
  ReadOnlyIcon,
  Spinner,
} from "@lemonadetraining/web-ui";

// -- own modules
import CoverArtPickAndPreview from "../ProgramCard/CoverArtPickAndPreview";
import ProgramControlPanel from "./ProgramControlPanel";
import { updateProductCoverURL, uploadCoverArt, updateProduct } from "@lemonadetraining/web-shared";
import { notifyActions, NOTIFICATION_TYPE } from "@lemonadetraining/web-shared";
import { messageForUploadError } from "@lemonadetraining/web-shared/utils/imageResize";

interface ProgramInfoProps {
  programData: {
    name?: string;
    description?: string;
    coverArtURL?: string;
    CoachName?: string;
    ownerName?: string;
    CoachUID?: string;
    pricing?: any;
    ownerUID?: string;
    productType?: string;
  };
  isLoading?: boolean;
  isItMyProgramme: boolean;
  staffViewOnly?: boolean;
  programUID: string;
}

function ProgramInfo({
  programData,
  isLoading,
  isItMyProgramme,
  staffViewOnly = false,
  programUID,
}: ProgramInfoProps) {
  const dispatch = useDispatch();
  // Storage only lets a user write under their own uid, so cover art is
  // uploaded under the signed-in editor, not the programme's creator.
  const editorUID: string | null = useSelector((state: any) => state.user.user?.uid ?? null);
  const [name, setName] = useState(programData.name || "");
  const [description, setDescription] = useState(programData.description || "");
  const [localLoading, setLocalLoading] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);

  /* ------------ [Handlers] ----------------- */
  const onProgramInfoUpdate = async () => {
    setLocalLoading(true);
    try {
      await updateProduct(programUID, programData.productType, {
        name,
        description,
      });
      const notification = {
        severity: NOTIFICATION_TYPE.success,
        title: "Programming updated!",
        message: `Programming  #${programUID} updated.`,
      };
      dispatch(notifyActions.sendNotification(notification));
    } catch (error: any) {
      const notification = {
        severity: NOTIFICATION_TYPE.error,
        title: "Error updating programming!",
        message: error.message || `Error creating updating programming.`,
      };
      dispatch(notifyActions.sendNotification(notification));
    } finally {
      setLocalLoading(false);
    }
  };

  const coverArtChanged = async (file: File) => {
    setLocalLoading(true);
    try {
      const coverURL = await uploadCoverArt(file, editorUID, programUID);
      if (coverURL) {
        await updateProductCoverURL(programUID, programData.productType, coverURL);
        const notification = {
          severity: NOTIFICATION_TYPE.success,
          title: "Cover Pic updated!",
          message: `Programming  #${programUID} updated.`,
        };
        dispatch(notifyActions.sendNotification(notification));
      }
    } catch (error: any) {
      const notification = {
        severity: NOTIFICATION_TYPE.error,
        title: "Error updating cover art!",
        message: messageForUploadError(
          error,
          "We couldn't update your cover art. Check your connection and try again.",
          { notAllowed: "You don't have permission to update cover art for this program." }
        ),
      };
      dispatch(notifyActions.sendNotification(notification));
    } finally {
      setLocalLoading(false);
    }
  };

  // The page places the cover beside this header, so the header is text only.
  if (staffViewOnly) {
    const creatorName = programData.ownerName || programData.CoachName;
    return (
      <div className="mt-2 flex flex-col gap-3">
        <h1 className="text-xl font-semibold">{programData.name}</h1>
        <Alert role="status">
          <ReadOnlyIcon aria-hidden="true" className="text-muted-foreground" />
          <AlertTitle>View only</AlertTitle>
          <AlertDescription className="col-start-2">
            {creatorName
              ? `${creatorName} and the gym's admins can edit this programme.`
              : "The coach who created it and the gym's admins can edit this programme."}
          </AlertDescription>
        </Alert>
        {programData.description && (
          <p className="whitespace-pre-line break-words text-sm">{programData.description}</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="mt-2 flex flex-row gap-2">
        <div className="flex flex-1 flex-col gap-2">
          {isItMyProgramme && (
            <div className="mr-4">
              <Input
                id="ProgrammingName"
                name="ProgrammingName"
                placeholder="Programming Name"
                value={name}
                onChange={(e) => {
                  if (!showUpdate) setShowUpdate(true);
                  setName(e.target.value);
                }}
                readOnly={!isItMyProgramme}
              />
            </div>
          )}
          <div className="mr-4">
            <Textarea
              id="ProgrammingDescription"
              name="ProgrammingDescription"
              placeholder="What this programming about"
              value={description}
              onChange={(e) => {
                if (!showUpdate) setShowUpdate(true);
                setDescription(e.target.value);
              }}
              readOnly={!isItMyProgramme}
              rows={8}
            />
          </div>
          {showUpdate && (
            <Button variant="outline" onClick={onProgramInfoUpdate} disabled={localLoading}>
              {localLoading ? (
                <>
                  <Spinner className="mr-2 size-4" />
                  Updating...
                </>
              ) : (
                "Update name and description"
              )}
            </Button>
          )}
        </div>

        <CoverArtPickAndPreview
          onCoverArtChanged={coverArtChanged}
          title={name}
          coverArtURL={programData.coverArtURL}
          coachName={programData.CoachName}
          coachUID={programData.CoachUID}
          pricing={programData.pricing}
          editable={isItMyProgramme}
        />
      </div>
      {isItMyProgramme && (
        <ProgramControlPanel
          isLoading={isLoading || localLoading}
          programUID={programUID}
          programData={programData}
        />
      )}
    </>
  );
}

export default ProgramInfo;
